import { FC, useEffect, useMemo, useState } from 'react'

import { Dropdown, Option, typography } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import {
  BrandRetailerConnection,
  BrandUserConnection,
  OrderBuyer,
} from '__generated__/atlas-types'

import { EMPTY_VALUE } from 'shop/products/traits'
import {
  getUserId,
  getUserName,
  isLiteRetailer as isLiteRetailerSelector,
} from 'store/currentUser/selectors'

import { OrderData } from 'features/Orders/Review/components'
import { ContactsIds } from 'features/Orders/Review/review.ids'
import { AddBuyerModal } from 'features/Orders/components/Contacts/AddBuyerModal/AddBuyerModal'

export enum EditableContactListTestIds {
  Component = 'editable-contact-list-component',
  BuyerDropdown = 'buyer-dropdown',
  SalesRepDropdown = 'sales-rep-dropdown',
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: visible;
`

const DropdownWithLinkWrapper = styled.div`
  position: relative;
  .link {
    position: absolute;
    top: 0;
    right: 0;

    cursor: pointer;
    ${typography.alpha.link.body3}
    &:visited:not(:hover) {
      color: var(--color-secondary-800);
    }
  }
`

const addOptionIfNotPresent = (
  options: Option[],
  newOption: Option,
): Option[] =>
  !options.some((option) => option.value === newOption.value)
    ? [newOption, ...options]
    : options

const sortSelectedFirst = (
  options: Option[],
  selectedOption?: Option,
): Option[] => {
  if (!selectedOption) {
    return options
  }
  const unselectedOptions = options.filter(
    (option) => option.value !== selectedOption.value,
  )
  return [selectedOption, ...unselectedOptions]
}

export enum EditableContactListFieldIds {
  BUYER = 'buyer',
  SALES_REP = 'salesRep',
}

export type EditableContactListProps = {
  onSelect?: (field: EditableContactListFieldIds, selected: Option) => void
  onAddBuyer?: () => void
  disableBuyerDropdown?: boolean
  disableSalesRepDropdown?: boolean
  brandBuyers?: BrandRetailerConnection
  brandSalesReps?: BrandUserConnection
  orderBuyer?: OrderBuyer
  orderSalesRep?: OrderData['salesRepresentative']
}

export const EditableContactList: FC<EditableContactListProps> = ({
  onSelect,
  onAddBuyer,
  disableBuyerDropdown = false,
  disableSalesRepDropdown = false,
  brandBuyers,
  brandSalesReps,
  orderBuyer,
  orderSalesRep,
}) => {
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const currentUserName = useSelector(getUserName)
  const currentUserId = useSelector(getUserId)

  const [selectedBuyer, setSelectedBuyer] = useState<Option>()
  const [selectedSalesRep, setSelectedSalesRep] = useState<Option>()

  const [isAddBuyerModalOpen, setBuyerModalOpen] = useState(false)

  useEffect(() => {
    if (orderBuyer) {
      setSelectedBuyer({
        label: orderBuyer.name ?? EMPTY_VALUE,
        value: orderBuyer.id,
      })
    }
  }, [orderBuyer])

  useEffect(() => {
    if (orderSalesRep) {
      setSelectedSalesRep({
        label: orderSalesRep.displayName || '',
        value: orderSalesRep.id,
      })
    }
  }, [orderSalesRep])

  // we decided to show: buyer selected during the checkout process in PHP (/cart/checkout)
  // + logged in retailer if you're logged as a lite retailer
  // + Brand's buyers
  const buyerOptions: Option[] = useMemo(() => {
    const brandBuyerOptions: Option[] =
      brandBuyers?.edges
        .flatMap((edge) => edge.buyers)
        .map((buyer) => ({
          label: buyer.name ?? EMPTY_VALUE,
          value: buyer.id,
        })) || []

    // this below is necessary because there are cases where the buyer assigned to the Order
    // is not present in the Brand's buyers, but we want it in the dropdown anyway
    const buyers = orderBuyer
      ? addOptionIfNotPresent(brandBuyerOptions, {
          value: orderBuyer.id,
          label: orderBuyer.name ?? EMPTY_VALUE,
        })
      : brandBuyerOptions

    if (isLiteRetailer) {
      const loggedInRetailer = {
        value: currentUserId,
        label: currentUserName,
      }
      const buyersWithoutLoggedInRetailer = buyers.filter(
        (buyer) => buyer.value !== currentUserId,
      ) // in case the logged retailer is already in the Brand's buyers,
      return sortSelectedFirst(
        [loggedInRetailer, ...buyersWithoutLoggedInRetailer],
        selectedBuyer,
      )
    }

    return sortSelectedFirst(buyers, selectedBuyer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandBuyers, orderBuyer, selectedBuyer])

  const salesRepOptions: Option[] = useMemo(() => {
    const brandSalesRepsOptions: Option[] =
      brandSalesReps?.edges.map((edge) => ({
        label: edge?.node?.displayName ?? EMPTY_VALUE,
        value: edge?.node?.id!,
      })) || []

    // this below is necessary because there are cases where the sales rep assigned to the Order
    // is not present in the Brand's sales reps, but we want it in the dropdown anyway
    const salesReps = orderSalesRep
      ? addOptionIfNotPresent(brandSalesRepsOptions, {
          value: orderSalesRep.id,
          label: orderSalesRep.displayName || '',
        })
      : brandSalesRepsOptions

    return sortSelectedFirst(salesReps, selectedSalesRep)
  }, [brandSalesReps, orderSalesRep, selectedSalesRep])

  return (
    <Container data-testid={EditableContactListTestIds.Component}>
      <Dropdown
        testId={EditableContactListTestIds.SalesRepDropdown}
        id={ContactsIds.SalesRepDropdownId}
        label="Sales Rep"
        value={selectedSalesRep}
        onSelect={({ selected }: { selected: Option }): void => {
          onSelect?.(EditableContactListFieldIds.SALES_REP, selected)
          setSelectedSalesRep(selected)
        }}
        options={salesRepOptions}
        isDisabled={!salesRepOptions.length || disableSalesRepDropdown}
      />
      <DropdownWithLinkWrapper>
        <div
          id={ContactsIds.CreateNewButtonId}
          className="link"
          onClick={() => setBuyerModalOpen(true)}
        >
          Create new
        </div>
        <Dropdown
          testId={EditableContactListTestIds.BuyerDropdown}
          id={EditableContactListFieldIds.BUYER}
          label="Buyer"
          value={selectedBuyer}
          onSelect={({ selected }: { selected: Option }): void => {
            onSelect?.(EditableContactListFieldIds.BUYER, selected)
            setSelectedBuyer(selected)
          }}
          options={buyerOptions}
          isDisabled={!buyerOptions.length || disableBuyerDropdown}
        />
      </DropdownWithLinkWrapper>
      {isAddBuyerModalOpen && (
        <AddBuyerModal
          isOpen={isAddBuyerModalOpen}
          onClose={() => setBuyerModalOpen(false)}
          onAddBuyer={onAddBuyer}
        />
      )}
    </Container>
  )
}
